/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { formatStoredDateTime } from '../utils/datetime';

const OpenPositions = ({
  openPositions,
  closeTrade,
  updateStopLoss,
  updateTakeProfit,
  partialClose,
  loadingTradeId,
  bare = false,
}) => {
  const isValidArray = Array.isArray(openPositions) && openPositions.length > 0;
  const [openDropdownPlatformId, setOpenDropdownPlatformId] = useState(null); // Using platform's position_id for UI state
  const dropdownRef = useRef(null);
  const anchorRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const navigate = useNavigate();

  const toggleMoreActions = (platformPositionId, event) => {
    setOpenDropdownPlatformId((prevOpenId) => {
      const next = prevOpenId === platformPositionId ? null : platformPositionId;
      if (next && event?.currentTarget) {
        anchorRef.current = event.currentTarget;
        setAnchorRect(event.currentTarget.getBoundingClientRect());
      } else if (!next) {
        anchorRef.current = null;
        setAnchorRect(null);
      }
      return next;
    });
  };

  // 🟢 FIX: Delay attaching the outside click handler so dropdown ref is set
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Allow "More" button clicks
      if (event.target.closest && event.target.closest('.more-actions-button')) return;

      // If a dropdown is open and click is outside both the dropdown and the anchor, close it
      const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      const clickedAnchor = anchorRef.current && anchorRef.current.contains(event.target);
      if (openDropdownPlatformId && !(clickedInsideDropdown || clickedAnchor)) {
        setOpenDropdownPlatformId(null);
        anchorRef.current = null;
        setAnchorRect(null);
      }
    };

    const handleReposition = () => {
      if (anchorRef.current) setAnchorRect(anchorRef.current.getBoundingClientRect());
    };

    let timeout;
    if (openDropdownPlatformId) {
      timeout = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleReposition, true);
        window.addEventListener('resize', handleReposition);
      }, 0);
    }
    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [openDropdownPlatformId]);

  const formatTime = (isoString) => {
    return formatStoredDateTime(isoString, undefined, '-');
  };

  const safeParseFloat = (value, toFixedDigits) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return num.toFixed(toFixedDigits);
  };

  const totalPL = isValidArray
    ? openPositions.reduce((sum, position) => {
        const pl = parseFloat(position.profit);
        return sum + (isNaN(pl) ? 0 : pl);
      }, 0)
    : 0;

  const widgetHeaderCellClassName = 'border-b widget-divider-border widget-header-fill px-2.5 py-2 text-[10px] font-medium uppercase tracking-[0.06em] text-textSecondary';
  const widgetBodyCellClassName = 'border-b widget-divider-border px-2.5 py-2 align-middle text-xs text-textPrimary';
  const widgetTextButtonClassName = 'inline-flex items-center justify-center rounded-md border widget-muted-border bg-backgroundSurface/35 px-2.5 py-1 text-[11px] font-medium text-textSecondary transition hover:bg-secondary/10 hover:text-textPrimary';
  const widgetDangerButtonClassName = 'inline-flex items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1 text-[11px] font-medium text-destructive transition hover:bg-destructive/15';

  const Table = (
    <div className="rounded-xl border widget-muted-border bg-backgroundSurface/20">
      <div className={`overflow-x-auto ${bare ? '' : '-mx-4 sm:mx-0'}`}>
        <table className={bare ? 'w-full min-w-[860px] border-collapse text-xs' : 'w-full min-w-[800px] border-collapse text-xs sm:text-sm'}>
        <thead>
          <tr>
            <th className={widgetHeaderCellClassName}>Position ID</th>
            <th className={widgetHeaderCellClassName}>Instrument</th>
            <th className={widgetHeaderCellClassName}>Direction</th>
            <th className={widgetHeaderCellClassName}>Lot Size</th>
            <th className={widgetHeaderCellClassName}>Entry</th>
            <th className={widgetHeaderCellClassName}>SL</th>
            <th className={widgetHeaderCellClassName}>TP</th>
            <th className={widgetHeaderCellClassName}>RR</th>
            <th className={`${widgetHeaderCellClassName} text-right`}>P/L</th>
            <th className={widgetHeaderCellClassName}>Time</th>
            <th className={widgetHeaderCellClassName} colSpan="2">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {isValidArray ? (
            openPositions.map((position, index) => {
              // Portal dropdown: compute placement based on viewport instead of table height
              const menuMaxHeight = 240; // px
              const spacing = 8; // gap between button and menu

              return (
                <tr key={position.ticket || index} className="transition-colors hover:bg-secondary/10">
                  <td className={`${widgetBodyCellClassName} text-center`}>{position.ticket}</td>
                  <td className={`${widgetBodyCellClassName} text-center font-medium`}>{position.symbol}</td>
                  <td className={`${widgetBodyCellClassName} text-center`}>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${position.direction === 'BUY' ? 'border-success/25 bg-success/10 text-success' : 'border-destructive/25 bg-destructive/10 text-destructive'}`}>
                      {position.direction}
                    </span>
                  </td>
                  <td className={`${widgetBodyCellClassName} text-center font-mono`}>{safeParseFloat(position.volume, 2)}</td>
                  <td className={`${widgetBodyCellClassName} text-center font-mono`}>{safeParseFloat(position.price_open, 5)}</td>
                  <td className={`${widgetBodyCellClassName} text-center font-mono`}>{position.sl ? safeParseFloat(position.sl, 5) : '-'}</td>
                  <td className={`${widgetBodyCellClassName} text-center font-mono`}>{position.tp ? safeParseFloat(position.tp, 5) : '-'}</td>
                  <td className={`${widgetBodyCellClassName} text-center font-mono`}>{position.rr_ratio ? safeParseFloat(position.rr_ratio, 2) : '-'}</td>
                  <td className={`${widgetBodyCellClassName} text-right font-mono font-medium ${parseFloat(position.profit) >= 0 ? 'text-success' : 'text-destructive'}`}>{position.profit !== null ? safeParseFloat(position.profit, 2) : '-'}</td>
                  <td className={`${widgetBodyCellClassName} whitespace-nowrap text-center text-textSecondary`}>{formatTime(position.time * 1000)}</td>
                  <td className={`${widgetBodyCellClassName} text-center`}>
                    {(() => {
                      const isLoadingThis = !!loadingTradeId && (
                        loadingTradeId === position.trade_id ||
                        loadingTradeId === position.ticket
                      );
                      const hasTradeId = !!position.trade_id;
                      return (
                        <button
                          className={`${widgetDangerButtonClassName} ${isLoadingThis ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={() => closeTrade(position.trade_id || null, {
                            close_reason: 'Manual',
                            ticket: position.ticket,
                            symbol: position.symbol,
                            volume: position.volume,
                            direction: position.direction,
                            orphan: !hasTradeId,
                          })}
                          disabled={isLoadingThis}
                          title={hasTradeId ? 'Close this trade' : 'Close this broker position (no linked Paxia trade record)'}
                        >
                          {isLoadingThis ? '...' : 'Close'}
                        </button>
                      );
                    })()}
                  </td>
                  <td className={`${widgetBodyCellClassName} text-center`}>
                    <button
                      className={`${widgetTextButtonClassName} more-actions-button ${loadingTradeId === position.trade_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={(e) => toggleMoreActions(position.ticket, e)}
                      disabled={loadingTradeId === position.trade_id}
                    >
                      More
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 inline-block ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openDropdownPlatformId === position.ticket && anchorRect && createPortal(
                      (() => {
                        // Decide whether to place above or below based on viewport space
                        const spaceBelow = window.innerHeight - anchorRect.bottom;
                        const placeAbove = spaceBelow < menuMaxHeight + spacing;
                        const top = placeAbove ? Math.max(8, anchorRect.top - menuMaxHeight - spacing) : Math.min(window.innerHeight - 8, anchorRect.bottom + spacing);
                        const left = Math.min(
                          // ensure menu stays within viewport horizontally
                          Math.max(8, anchorRect.right - 192), // align right edge by default (menu width ~192px)
                          window.innerWidth - 200
                        );
                        return (
                          <div
                            ref={dropdownRef}
                            style={{
                              position: 'fixed',
                              top,
                              left,
                              width: 192,
                              maxHeight: menuMaxHeight,
                              zIndex: 10000,
                            }}
                            className="rounded-lg border widget-muted-border bg-backgroundSurface shadow-lg py-1 overflow-y-auto"
                          >
                            <button
                              className="block w-full px-4 py-2 text-left text-sm text-textPrimary transition hover:bg-secondary/10"
                              onClick={() => { updateStopLoss(position); setOpenDropdownPlatformId(null); }}
                            >
                              Update SL
                            </button>
                            <button
                              className="block w-full px-4 py-2 text-left text-sm text-textPrimary transition hover:bg-secondary/10"
                              onClick={() => { updateTakeProfit(position); setOpenDropdownPlatformId(null); }}
                            >
                              Update TP
                            </button>
                            <button
                              className="block w-full px-4 py-2 text-left text-sm text-textPrimary transition hover:bg-secondary/10"
                              onClick={() => { partialClose(position); setOpenDropdownPlatformId(null); }}
                            >
                              Partial Close
                            </button>
                            {position.trade_id && (
                              <button
                                className="block w-full px-4 py-2 text-left text-sm text-textPrimary transition hover:bg-secondary/10"
                                onClick={() => { navigate(`/trades/history/${position.trade_id}`); setOpenDropdownPlatformId(null); }}
                              >
                                Trade Journal
                              </button>
                            )}
                          </div>
                        );
                      })(),
                      document.body
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="12" className="px-3 py-10 text-center text-sm text-textSecondary">No open positions to show.</td>
            </tr>
          )}
        </tbody>
        </table>
      </div>
    </div>
  );

  if (bare) {
    return (
      <div className="w-full space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex items-center rounded-full border widget-muted-border bg-backgroundSurface/30 px-3 py-1 text-[11px] font-medium text-textSecondary">
            {isValidArray ? `${openPositions.length} positions` : '0 positions'}
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border widget-muted-border bg-backgroundSurface/30 px-3 py-1 text-[11px] text-textSecondary">
            <span className="font-medium">Total P/L</span>
            <span className={`font-mono font-semibold ${totalPL >= 0 ? 'text-success' : 'text-destructive'}`}>{safeParseFloat(totalPL, 2)}</span>
          </div>
        </div>
        {Table}
      </div>
    );
  }

  // Default panel style for non-widget usage (e.g., Dashboard)
  return (
    <div className="rounded-xl border widget-muted-border bg-backgroundSurface/20 p-4 sm:p-5 mt-4 sm:mt-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-base sm:text-lg font-medium text-textPrimary">Open Positions</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center rounded-full border widget-muted-border bg-backgroundSurface/30 px-3 py-1 text-[11px] font-medium text-textSecondary">
            {isValidArray ? `${openPositions.length} positions` : '0 positions'}
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border widget-muted-border bg-backgroundSurface/30 px-3 py-1 text-[11px] text-textSecondary">
            <span className="font-medium">Total P/L</span>
            <span className={`font-mono font-semibold ${totalPL >= 0 ? 'text-success' : 'text-destructive'}`}>{safeParseFloat(totalPL, 2)}</span>
          </div>
        </div>
      </div>
      {Table}
    </div>
  );
};

export default OpenPositions;
